import { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import {
  useGetAllCategories,
  useUpdateCategoryMutation,
} from "../../../../../api/jeep-rally/categories/categories.hooks";
import { useGetAllTypes } from "../../../../../api/jeep-rally/types/types.hooks";
import { normalizeTypes } from "../../Types/utils/type.utils";
import CategoryForm from "../_components/CategoryForm";
import {
  buildCategoryFormData,
  getCategoryApiErrorMessage,
  initialCategoryForm,
  isJeepRallyAdmin,
  mapCategoryToForm,
  normalizeCategories,
  validateCategoryForm,
} from "../utils/category.utils";

const CategoryUpdatePage = () => {
  const history = useHistory();
  const { id } = useParams();
  const canManage = isJeepRallyAdmin();
  const locationCategory = history.location.state?.category || null;

  const [form, setForm] = useState(
    locationCategory ? mapCategoryToForm(locationCategory) : initialCategoryForm
  );
  const [initialForm, setInitialForm] = useState(
    locationCategory ? mapCategoryToForm(locationCategory) : initialCategoryForm
  );
  const [validationErrors, setValidationErrors] = useState({});

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    error: categoriesError,
  } = useGetAllCategories();

  const categories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData]
  );

  const {
    data: typesData,
    isLoading: isTypesLoading,
    isError: isTypesError,
  } = useGetAllTypes();
  const types = useMemo(() => normalizeTypes(typesData), [typesData]);

  const category = useMemo(() => {
    return (
      categories.find((item) => (item._id || item.id) === id) ||
      (locationCategory &&
      (locationCategory._id || locationCategory.id) === id
        ? locationCategory
        : null)
    );
  }, [categories, id, locationCategory]);

  const {
    mutate: updateCategory,
    isLoading,
    isPending,
    error,
    reset,
  } = useUpdateCategoryMutation();

  const submitting = isLoading || isPending;

  useEffect(() => {
    if (!category) return;
    const nextForm = mapCategoryToForm(category);
    setForm((prev) => ({
      ...nextForm,
      imageFile: prev.imageFile,
    }));
    setInitialForm(nextForm);
  }, [category]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canManage || !id) return;
    reset();

    const nextErrors = validateCategoryForm(form, {
      existingCategories: categories,
      currentId: id,
    });
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const formData = buildCategoryFormData(form, {
      isEdit: true,
      initialForm,
    });

    if (!formData) {
      history.push("/jeep-rally/categories");
      return;
    }

    updateCategory(
      { categoryId: id, formData },
      {
        onSuccess: () => {
          history.push("/jeep-rally/categories");
        },
      }
    );
  };

  const goBack = () => history.push("/jeep-rally/categories");

  if (isCategoriesLoading && !locationCategory) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isCategoriesError && !locationCategory) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getCategoryApiErrorMessage(
                categoriesError,
                "Failed to load category details."
              )}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  if (!isCategoriesLoading && !category) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              Category not found.
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to categories list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Update Category</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Update category details. Upload a new image to replace the current
            one. The key cannot be changed.
          </p>

          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to update categories.
            </Alert>
          )}

          {error && (
            <Alert variant="danger" className="mb-4">
              {getCategoryApiErrorMessage(
                error,
                "Failed to update category. Please try again."
              )}
            </Alert>
          )}

          <CategoryForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            submitLabel="Update Category"
            submittingLabel="Updating..."
            isEditMode
            readOnly={!canManage}
            types={types}
            isTypesLoading={isTypesLoading}
            isTypesError={isTypesError}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={goBack}
          />
        </div>
      </div>
    </div>
  );
};

export default CategoryUpdatePage;
